import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmIconComponent } from '@lite-workspace/shared/helm-ui/icon';
import { PHOTOBOOK_MENU_ENABLED, isProductOffered } from '@lite-workspace/shared/helm-ui/product-switcher';
import { LUMIR_PRODUCTS } from '@environment';
import { NotificationRowComponent } from './notification-row.component';
import { NotificationsStore } from './notifications.store';
import {
  NOTIFICATIONS_HOST_PRODUCT,
  NOTIFICATION_PRODUCT_FILTER_LABELS,
  resolveNotificationNavigation,
} from './notifications.models';
import type { NotificationItem, NotificationProductFilter } from './notifications.models';

interface FilterChip {
  value: NotificationProductFilter;
  label: string;
}

// The unified feed (spec 17 §3.1): reverse-chronological, one-click product
// filter chips, per-row read on click, «Прочитать все» at the top. One page for
// every cabinet — the feed is cross-product, so nothing here is app-specific.
@Component({
  selector: 'app-notifications-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, HlmIconComponent, RouterLink, NotificationRowComponent],
  template: `
    <div class="mx-auto w-full max-w-[720px] px-5 pb-16 pt-8">
      <div class="flex items-center justify-between gap-3">
        <h1 class="text-xl font-semibold text-ink">Уведомления</h1>
        <div class="flex items-center gap-2">
          @if (store.unreadCount() > 0) {
            <button hlmBtn type="button" variant="outline" class="h-8 rounded-full px-3 text-sm" (click)="store.markAllRead()">
              Прочитать все
            </button>
          }
          <a
            routerLink="/notifications/settings"
            aria-label="Настройки уведомлений"
            class="grid size-8 place-items-center rounded-full border border-line2 bg-panel3 text-c2 hover:text-ink"
          >
            <hlm-icon src="/assets/icons/gear.svg" [size]="15" />
          </a>
        </div>
      </div>

      <div class="mt-4 flex flex-wrap items-center gap-1.5">
        @for (chip of chips(); track chip.label) {
          <button
            type="button"
            class="h-7 cursor-pointer rounded-full border px-3 text-sm"
            [class.border-acc-line]="isActive(chip.value)"
            [class.bg-acc-soft]="isActive(chip.value)"
            [class.text-acc2]="isActive(chip.value)"
            [class.border-line2]="!isActive(chip.value)"
            [class.text-c2]="!isActive(chip.value)"
            [attr.aria-pressed]="isActive(chip.value)"
            (click)="store.setProductFilter(chip.value)"
          >
            {{ chip.label }}
          </button>
        }
      </div>

      <div class="mt-4 rounded-[var(--r)] border border-line bg-panel2 p-2">
        @switch (store.feedStatus()) {
          @case ('loading') {
            <p class="px-3 py-10 text-center text-sm text-c3">Загрузка…</p>
          }
          @case ('error') {
            <div class="flex flex-col items-center gap-3 px-3 py-10">
              <p class="text-sm text-c3">Не удалось загрузить уведомления.</p>
              <button hlmBtn type="button" variant="outline" class="h-8 rounded-full px-3 text-sm" (click)="store.loadFeed(1)">
                Повторить
              </button>
            </div>
          }
          @default {
            @if (store.feed().length === 0) {
              <div class="flex flex-col items-center gap-2 px-3 py-12">
                <hlm-icon src="/assets/icons/bell.svg" [size]="24" class="text-c3" />
                <p class="text-sm font-semibold text-c2">Пока нет уведомлений</p>
                <p class="text-sm text-c3">События появятся здесь.</p>
              </div>
            } @else {
              @for (item of store.feed(); track item.id) {
                <app-notification-row [item]="item" (activate)="onActivate(item)" />
              }
            }
          }
        }
      </div>

      @if (store.feedTotalPages() > 1) {
        <div class="mt-4 flex items-center justify-center gap-3">
          <button
            hlmBtn
            type="button"
            variant="outline"
            class="h-8 rounded-full px-3 text-sm"
            [disabled]="store.feedPage() <= 1"
            (click)="store.loadFeed(store.feedPage() - 1)"
          >
            Назад
          </button>
          <span class="text-sm text-c3">{{ store.feedPage() }} / {{ store.feedTotalPages() }}</span>
          <button
            hlmBtn
            type="button"
            variant="outline"
            class="h-8 rounded-full px-3 text-sm"
            [disabled]="store.feedPage() >= store.feedTotalPages()"
            (click)="store.loadFeed(store.feedPage() + 1)"
          >
            Вперёд
          </button>
        </div>
      }
    </div>
  `,
})
export class NotificationsPageComponent {
  protected readonly store = inject(NotificationsStore);
  private readonly router = inject(Router);
  private readonly hostProduct = inject(NOTIFICATIONS_HOST_PRODUCT);

  private readonly photobookMenuEnabled = inject(PHOTOBOOK_MENU_ENABLED);

  // A product chip follows the product menu: a product the menu hides (Фотобук on prod, a product with no
  // address there — CRM) would only ever show an empty feed. Like the menu, the host's own product always
  // keeps its chip. Inbox tags spell the product id with `_` (`studio_manager`).
  protected readonly chips = computed<FilterChip[]>(() => [
    { value: null, label: 'Все' },
    ...Object.entries(NOTIFICATION_PRODUCT_FILTER_LABELS)
      .filter(([value]) => this.chipOffered(value))
      .map(([value, label]) => ({ value, label })),
  ]);

  constructor() {
    void this.store.loadFeed(1);
    void this.store.refreshUnreadCount();
  }

  protected isActive(value: NotificationProductFilter): boolean {
    return this.store.productFilter() === value;
  }

  private chipOffered(tag: string): boolean {
    const product = LUMIR_PRODUCTS.find(p => p.id === tag.replace('_', '-'));
    return !product || tag === this.hostProduct || isProductOffered(product, this.photobookMenuEnabled);
  }

  protected onActivate(item: NotificationItem): void {
    void this.store.markRead(item.id);
    const nav = resolveNotificationNavigation(item, this.hostProduct);
    if (nav?.kind === 'internal') void this.router.navigateByUrl(nav.url);
    else if (nav?.kind === 'external') window.open(nav.url, '_blank', 'noopener');
  }
}
